import type {
  Comment as WeaverComment,
  CommentImportStatus,
} from '~/api/weaver/typedefs.autogen';
import type { ContentAst } from '~/eds/src/types/ContentAst';
import type { Entity } from '~/eds/src/types/Entity';
import type { EntityType } from '~/eds/src/types/EntityType';
import type { Comment, CommentData } from '~/types/Comment';

import { endpoints as autogenEnhancements } from '~/api/weaver/enhancements.autogen';
import { Tags } from '~/api/weaver/tags.autogen';
import { api } from '~/redux/api/createApi';

import { TagType } from '../TagType';
import { mapWeaverError } from './mapError';
import { weaverApiRoot } from './root';

weaverApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

export const COMMENT_TAG = { type: Tags.Comment, id: 'LIST' };

const transformComment = (payload: WeaverComment): Comment => ({
  id: payload.id,
  content: payload.content as ContentAst,
  context: (payload.context ?? []) as Comment['context'],
  createdBy: payload.createdBy,
  creatorName: payload.creatorName ?? null,
  createdDate: new Date(payload.createdDate),
  entity: {
    type: payload.entityType as EntityType,
    id: payload.entityId,
  },
  isResolved: payload.isResolved,
  modifiedDate: new Date(payload.modifiedDate),
  mentions:
    payload.mentions
      ?.filter((mention) => mention.type === 'user')
      .map(({ id }) => ({ type: 'user', id: String(id) })) ?? [],
  replies: 0,
  threadId: payload.threadId,
});

/**
 * @lintignore Not imported by name anywhere, but this export must exist: dropping it
 * (or knip's fix un-exporting/deleting it) risks the module never being evaluated, which
 * would mean `injectEndpoints` never registers these endpoints on the shared root api.
 */
export const weaverApi = weaverApiRoot.injectEndpoints({
  endpoints: (build) => ({
    getComments: build.query<Comment[], Entity>({
      query: ({ id, type }) => ({
        url: `/${type}/${id}/comments`,
      }),
      transformResponse: (response: WeaverComment[]) =>
        response.map(transformComment),
      transformErrorResponse: mapWeaverError,
      providesTags: [COMMENT_TAG],
    }),
    createComment: build.mutation<Comment, CommentData>({
      query: (comment) => ({
        url: `/${comment.entity.type}/${comment.entity.id}/comments`,
        method: 'POST',
        body: {
          content: comment.content,
          context: comment.context,
          entityId: comment.entity.id,
          entityType: comment.entity.type,
          mentions: comment.mentions,
          threadId: comment.threadId,
        },
      }),
      transformResponse: transformComment,
      transformErrorResponse: mapWeaverError,
      invalidatesTags: [COMMENT_TAG],
    }),
    updateComment: build.mutation<Comment, Comment>({
      query: (comment) => ({
        url: `/${comment.entity.type}/${comment.entity.id}/comments/${comment.id}`,
        method: 'PATCH',
        body: {
          content: comment.content,
          mentions: comment.mentions,
        },
      }),
      transformResponse: transformComment,
      transformErrorResponse: mapWeaverError,
      invalidatesTags: [COMMENT_TAG],
    }),
    deleteComment: build.mutation<void, Comment>({
      query: (comment) => ({
        url: `/${comment.entity.type}/${comment.entity.id}/comments/${comment.id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: mapWeaverError,
      invalidatesTags: [COMMENT_TAG],
      onQueryStarted: async (_comment, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            api.util.invalidateTags([{ type: TagType.Highlight, id: 'LIST' }]),
          );
        } catch {}
      },
    }),
    resolveComment: build.mutation<void, Comment>({
      query: (comment) => ({
        url: `/${comment.entity.type}/${comment.entity.id}/comments/${comment.id}/resolve`,
        method: 'POST',
      }),
      transformErrorResponse: mapWeaverError,
      invalidatesTags: [COMMENT_TAG],
    }),
    unresolveComment: build.mutation<void, Comment>({
      query: (comment) => ({
        url: `/${comment.entity.type}/${comment.entity.id}/comments/${comment.id}/unresolve`,
        method: 'POST',
      }),
      transformErrorResponse: mapWeaverError,
      invalidatesTags: [COMMENT_TAG],
    }),
    getCommentImportStatus: build.query<
      CommentImportStatus,
      { documentId: string; versionId: string }
    >({
      query: ({ documentId, versionId }) => ({
        url: `/document/${documentId}/version/${versionId}/comment-import-status`,
        rtkQueryOptions: { showErrorToast: false },
      }),
      transformErrorResponse: mapWeaverError,
    }),
  }),
  overrideExisting: true,
});
